import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Paper, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  IconButton, 
  Grid, 
  CircularProgress,
  InputAdornment,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Autocomplete,
  FormHelperText,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';

// Para transferi modal penceresi için props arayüzü
interface CashTransferModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  onSuccess?: () => void;
  initialTransferDirection?: TransferDirection; // Yeni prop ekleyelim
}

// Banka hesabı arayüzü
interface BankAccount {
  id: string;
  bankName: string;
  accountNumber: string;
  currency: string;
  balance: number;
  subAccounts?: { [key: string]: SubAccount };
  isSubAccount?: boolean;
  parentId?: string;
}

// Alt hesap arayüzü
interface SubAccount {
  id: string;
  accountName: string;
  accountNumber: string;
  currency: string;
  balance: number;
  parentId: string;
}

// Kasa arayüzü
interface CashboxItem {
  id: string;
  name: string;
  currency: string;
  balance: number;
}

// Cari hesap arayüzü
interface Account {
  id: string;
  code: string;
  name: string;
  currency: string;
  balance: number;       // Eski sistem için tutuldu
  debitBalance: number;  // Borç bakiyesi (bizim onlardan alacağımız)
  creditBalance: number; // Alacak bakiyesi (onların bizden alacağı)
  type: 'customer' | 'supplier';
  companyName?: string;
}

// Transfer yönleri
type TransferDirection = 'bank_to_cash' | 'cash_to_bank' | 'cash_to_cash' | 'bank_to_bank' | 'account_to_account';

const CashTransferModal: React.FC<CashTransferModalProps> = ({
  open,
  onClose,
  companyId,
  onSuccess,
  initialTransferDirection
}) => {
  // Temel state tanımlamaları
  const [amount, setAmount] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [documentNo, setDocumentNo] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Transfer yönü (varsayılan: bankadan kasaya veya props ile gelen değer)
  const [transferDirection, setTransferDirection] = useState<TransferDirection>(initialTransferDirection || 'bank_to_cash');
  
  // Banka hesapları ve kasalar
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [cashboxes, setCashboxes] = useState<CashboxItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  
  // Seçili hesaplar
  const [selectedBankAccount, setSelectedBankAccount] = useState<string>('');
  const [selectedCashbox, setSelectedCashbox] = useState<string>('');
  const [selectedTargetCashbox, setSelectedTargetCashbox] = useState<string>('');
  const [selectedTargetBankAccount, setSelectedTargetBankAccount] = useState<string>('');
  
  // Seçili hesapların detayları
  const [bankAccountDetails, setBankAccountDetails] = useState<BankAccount | null>(null);
  const [cashboxDetails, setCashboxDetails] = useState<CashboxItem | null>(null);
  const [targetCashboxDetails, setTargetCashboxDetails] = useState<CashboxItem | null>(null);
  const [targetBankAccountDetails, setTargetBankAccountDetails] = useState<BankAccount | null>(null);

  // Cari hesaplar
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [selectedTargetAccount, setSelectedTargetAccount] = useState<string>('');
  const [accountDetails, setAccountDetails] = useState<Account | null>(null);
  const [targetAccountDetails, setTargetAccountDetails] = useState<Account | null>(null);

  // Modal açıldığında verileri yükle
  useEffect(() => {
    if (open && companyId) {
      loadBankAccounts();
      loadCashboxes();
      loadAccounts();
      
      // Eğer initialTransferDirection değeri geldiyse, transfer yönünü güncelle
      if (initialTransferDirection) {
        setTransferDirection(initialTransferDirection);
    }
    }
  }, [open, companyId, initialTransferDirection]);

  // Modal kapanınca formu sıfırla
  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  // Formu sıfırlama
  const resetForm = () => {
    setAmount('');
    setDescription('');
    setDocumentNo('');
    setError(null);
    setSuccessMessage(null);
    setTransferDirection(initialTransferDirection || 'bank_to_cash');
    setSelectedBankAccount('');
    setSelectedCashbox('');
    setSelectedTargetCashbox('');
    setSelectedTargetBankAccount('');
    setBankAccountDetails(null);
    setCashboxDetails(null);
    setTargetCashboxDetails(null);
    setTargetBankAccountDetails(null);
    setSelectedAccount('');
    setSelectedTargetAccount('');
    setAccountDetails(null);
    setTargetAccountDetails(null);
  };

  // Bankalar yükleniyor (SQL'den)
  const loadBankAccounts = async () => {
    setLoading(true);
    try {
      console.log('🏦 Banka hesapları SQL\'den yükleniyor...', companyId);
      
      const response = await accountingApi.get(ACCOUNTING_API.BANK_ACCOUNT_BY_COMPANY);
      console.log('📦 SQL Response:', response.data);
      
      const bankAccountsData = response.data.data || response.data;
      
      if (Array.isArray(bankAccountsData) && bankAccountsData.length > 0) {
        const accounts: BankAccount[] = [];
        
        // Ana hesapları ve alt hesapları ekle
        bankAccountsData.forEach((accountData: any) => {
          // Ana hesabı ekle
          accounts.push({
            id: accountData.id.toString(), // SQL ID'si string'e çevir
            bankName: accountData.bankName || '',
            accountNumber: accountData.accountNumber || '',
            currency: accountData.currency || 'TRY',
            balance: accountData.balance || 0,
            subAccounts: undefined, // SQL'de array olarak geliyor
            isSubAccount: false
          });
          
          // Alt hesapları ekle (array formatında geliyor)
          if (accountData.subAccounts && Array.isArray(accountData.subAccounts)) {
            accountData.subAccounts.forEach((subData: any) => {
              accounts.push({
                id: `${accountData.id}_${subData.id}`, // Alt hesap için birleşik ID
                bankName: `${accountData.bankName} / ${subData.accountName}`,
                accountNumber: subData.accountNumber || '',
                currency: subData.currency || 'TRY',
                balance: subData.balance || 0,
                isSubAccount: true,
                parentId: accountData.id.toString()
              });
            });
          }
        });
        
        // Banka adına göre sırala
        accounts.sort((a, b) => a.bankName.localeCompare(b.bankName));
        
        console.log('✅ SQL\'den yüklenen banka hesapları:', accounts);
        setBankAccounts(accounts);
        
        // İlk banka hesabını otomatik seç
        if (accounts.length > 0 && !selectedBankAccount) {
          setSelectedBankAccount(accounts[0].id);
          setBankAccountDetails(accounts[0]);
        }
      } else {
        console.log('ℹ️ Banka hesabı bulunamadı');
        setBankAccounts([]);
      }
    } catch (error) {
      console.error('❌ Banka hesapları yüklenirken hata:', error);
      setError('Banka hesapları yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  // Kasalar yükleniyor (SQL'den)
  const loadCashboxes = async () => {
    setLoading(true);
    try {
      console.log('💰 Kasalar SQL\'den yükleniyor...', companyId);
      
      const response = await accountingApi.get(ACCOUNTING_API.CASHBOX_BY_COMPANY);
      console.log('📦 SQL Response:', response.data);
      
      const cashboxesData = response.data.data || response.data;
      
      if (Array.isArray(cashboxesData) && cashboxesData.length > 0) {
        const boxes: CashboxItem[] = cashboxesData.map((cashboxData: any) => ({
          id: cashboxData.id.toString(), // SQL ID'si string'e çevir
          name: cashboxData.name || '',
          currency: cashboxData.currency || 'TRY',
          balance: cashboxData.balance || 0
        }));
        
        // Kasa adına göre sırala
        boxes.sort((a, b) => a.name.localeCompare(b.name));
        
        console.log('✅ SQL\'den yüklenen kasalar:', boxes);
        setCashboxes(boxes);
        
        // İlk kasayı otomatik seç
        if (boxes.length > 0 && !selectedCashbox) {
          setSelectedCashbox(boxes[0].id);
          setCashboxDetails(boxes[0]);
        }
      } else {
        console.log('ℹ️ Kasa bulunamadı');
        setCashboxes([]);
      }
    } catch (error) {
      console.error('❌ Kasalar yüklenirken hata:', error);
      setError('Kasalar yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  // Cari hesapları yükle (SQL API)
  const loadAccounts = async () => {
    setLoading(true);
    try {
      console.log('📥 Cari hesaplar yükleniyor (SQL)...');
      
      const response = await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_COMPANY);
      console.log('🔍 SQL API yanıtı:', response.data);

      if (response.data.success && response.data.data && Array.isArray(response.data.data)) {
        const accountList: Account[] = response.data.data
          .filter((accountData: any) => accountData.isActive && !accountData.isDeleted)
          .map((accountData: any) => ({
            id: accountData.id.toString(), // SQL ID'si string'e çevir
            code: accountData.accountCode || '',
            name: accountData.companyName || '',
            companyName: accountData.companyName || '',
            currency: 'TRY', // Backend'den gelmiyor, sabit TRY
            balance: accountData.balance || 0,
            debitBalance: accountData.debitBalance || 0,
            creditBalance: accountData.creditBalance || 0,
            type: 'customer'
          }));
        
        // Şirket adına göre sırala
        accountList.sort((a, b) => (a.companyName || a.name).localeCompare(b.companyName || b.name));
        
        console.log('✅ SQL\'den yüklenen cari hesaplar:', accountList);
        setAccounts(accountList);
        
        // İlk cariyi otomatik seç (varsa)
        if (accountList.length > 0 && transferDirection === 'account_to_account' && !selectedAccount) {
          setSelectedAccount(accountList[0].id);
          setAccountDetails(accountList[0]);
          
          // İkinci cariyi de seç (varsa başka)
          if (accountList.length > 1) {
            setSelectedTargetAccount(accountList[1].id);
            setTargetAccountDetails(accountList[1]);
          }
        }
      } else {
        console.log('ℹ️ Cari hesap bulunamadı');
        setAccounts([]);
      }
    } catch (error) {
      console.error('❌ Cari hesaplar yüklenirken hata:', error);
      setError('Cari hesaplar yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  // Transfer yönü değiştiğinde
  const handleDirectionChange = (event: SelectChangeEvent) => {
    setTransferDirection(event.target.value as TransferDirection);
  };

  // Para birimi sembolünü döndür
  const getCurrencySymbol = (currency: string): string => {
    switch(currency) {
      case 'TRY': return '₺';
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'JPY': return '¥';
      case 'CHF': return 'Fr.';
      case 'AUD': return 'A$';
      case 'CAD': return 'C$';
      case 'CNY': return '¥';
      default: return currency;
    }
  };

  // Banka hesabı seçildiğinde
  const handleBankAccountChange = (event: SelectChangeEvent) => {
    const accountId = event.target.value;
    setSelectedBankAccount(accountId);
    
    // Seçilen hesabın detaylarını bul
    const account = bankAccounts.find(acc => acc.id === accountId);
    setBankAccountDetails(account || null);
  };

  // Kasa seçildiğinde
  const handleCashboxChange = (event: SelectChangeEvent) => {
    const cashboxId = event.target.value;
    setSelectedCashbox(cashboxId);
    
    // Seçilen kasanın detaylarını bul
    const cashbox = cashboxes.find(cash => cash.id === cashboxId);
    setCashboxDetails(cashbox || null);
  };

  // Hedef kasa seçildiğinde
  const handleTargetCashboxChange = (event: SelectChangeEvent) => {
    const cashboxId = event.target.value;
    setSelectedTargetCashbox(cashboxId);
    
    // Seçilen kasanın detaylarını bul
    const cashbox = cashboxes.find(cash => cash.id === cashboxId);
    setTargetCashboxDetails(cashbox || null);
  };

  // Hedef banka hesabı seçildiğinde
  const handleTargetBankAccountChange = (event: SelectChangeEvent) => {
    const accountId = event.target.value;
    setSelectedTargetBankAccount(accountId);
    
    // Seçilen hesabın detaylarını bul
    const account = bankAccounts.find(acc => acc.id === accountId);
    setTargetBankAccountDetails(account || null);
  };

  // Sayısal girdi kontrolü
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Sayısal değer ve nokta dışındaki karakterleri engelle
    const value = e.target.value.replace(/[^0-9.]/g, '');
    
    // Birden fazla nokta olmasını engelle
    if (value.split('.').length > 2) {
      return;
    }
    
    setAmount(value);
  };

  // Transfer işlemi
  const handleTransfer = async () => {
    // Validasyon işlemleri
    if (!amount || parseFloat(amount) <= 0) {
      setError('Lütfen geçerli bir miktar giriniz.');
      return;
    }
    
    if (transferDirection === 'bank_to_cash' || transferDirection === 'cash_to_bank') {
      if (!selectedBankAccount) {
        setError('Lütfen bir banka hesabı seçiniz.');
        return;
      }
      
      if (!selectedCashbox) {
        setError('Lütfen bir kasa seçiniz.');
        return;
      }
      
      // Para birimi kontrolü
      if (bankAccountDetails?.currency !== cashboxDetails?.currency) {
        setError('Para transferi sadece aynı para birimindeki hesaplar arasında yapılabilir.');
        return;
      }
      
      // Bankadan kasaya transferde, banka bakiyesi kontrolü
      if (transferDirection === 'bank_to_cash' && 
          bankAccountDetails && 
          parseFloat(amount) > bankAccountDetails.balance) {
        setError('Banka hesabında yeterli bakiye bulunmamaktadır.');
        return;
      }
      
      // Kasadan bankaya transferde, kasa bakiyesi kontrolü
      if (transferDirection === 'cash_to_bank' && 
          cashboxDetails && 
          parseFloat(amount) > cashboxDetails.balance) {
        setError('Kasada yeterli bakiye bulunmamaktadır.');
        return;
      }
    } else if (transferDirection === 'cash_to_cash') {
      if (!selectedCashbox) {
        setError('Lütfen bir kaynak kasa seçiniz.');
        return;
      }
      
      if (!selectedTargetCashbox) {
        setError('Lütfen bir hedef kasa seçiniz.');
        return;
      }
      
      // Aynı kasa kontrolü
      if (selectedCashbox === selectedTargetCashbox) {
        setError('Aynı kasa arasında transfer yapamazsınız.');
        return;
      }
      
      // Para birimi kontrolü
      if (cashboxDetails?.currency !== targetCashboxDetails?.currency) {
        setError('Para transferi sadece aynı para birimindeki kasalar arasında yapılabilir.');
        return;
      }
      
      // Kasadan transfer etmek için yeterli bakiye var mı?
      if (cashboxDetails && parseFloat(amount) > cashboxDetails.balance) {
        setError('Kaynak kasada yeterli bakiye bulunmamaktadır.');
        return;
      }
    } else if (transferDirection === 'bank_to_bank') {
      if (!selectedBankAccount) {
        setError('Lütfen bir kaynak banka hesabı seçiniz.');
        return;
      }
      
      if (!selectedTargetBankAccount) {
        setError('Lütfen bir hedef banka hesabı seçiniz.');
        return;
      }
      
      if (selectedBankAccount === selectedTargetBankAccount) {
        setError('Kaynak ve hedef banka hesabı aynı olamaz.');
        return;
      }
      
      // Para birimi kontrolü
      if (bankAccountDetails?.currency !== targetBankAccountDetails?.currency) {
        setError('Para transferi sadece aynı para birimindeki hesaplar arasında yapılabilir.');
        return;
      }
      
      // Kaynak bankada yeterli bakiye var mı kontrolü
      if (bankAccountDetails && parseFloat(amount) > bankAccountDetails.balance) {
        setError('Kaynak banka hesabında yeterli bakiye bulunmamaktadır.');
        return;
      }
    } else if (transferDirection === 'account_to_account') {
      if (!selectedAccount) {
        setError('Lütfen bir kaynak cari hesap seçiniz.');
        return;
      }
      
      if (!selectedTargetAccount) {
        setError('Lütfen bir hedef cari hesap seçiniz.');
        return;
      }
      
      if (selectedAccount === selectedTargetAccount) {
        setError('Kaynak ve hedef cari hesap aynı olamaz.');
        return;
      }
      
      // Para birimi kontrolü
      if (accountDetails?.currency !== targetAccountDetails?.currency) {
        setError('Para transferi sadece aynı para birimindeki hesaplar arasında yapılabilir.');
        return;
      }
      
      // Borç/alacak bakiyesi kontrolü
      const transferAmountValue = parseFloat(amount);
      
      // İşlem türünü belirle: Kaynak hesapta borç bakiyesi mi, alacak bakiyesi mi kullanılacak
      if (accountDetails && accountDetails.creditBalance && accountDetails.creditBalance > 0) {
        // Kaynak hesapta alacak bakiyesi (creditBalance) varsa
        if (accountDetails.creditBalance < transferAmountValue) {
          setError('Kaynak cari hesapta yeterli alacak bakiyesi (creditBalance) bulunmamaktadır.');
          return;
        }
      } else if (accountDetails && accountDetails.debitBalance && accountDetails.debitBalance > 0) {
        // Kaynak hesapta borç bakiyesi (debitBalance) varsa
        if (accountDetails.debitBalance < transferAmountValue) {
          setError('Kaynak cari hesapta yeterli borç bakiyesi (debitBalance) bulunmamaktadır.');
          return;
        }
      } else {
        setError('Kaynak cari hesapta transfer edilecek bakiye bulunamadı.');
        return;
      }
    }
    
    // İşleme başla ve durumu güncelle
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      // 1. Kullanıcı bilgisini al
      const uid = ownAuthSession.getCurrentUid();
      if (!uid) {
        throw new Error("Kullanıcı oturumu bulunamadı");
      }
      const userId = uid;
      const userEmail = ownAuthSession.getCurrentEmail() || 'unknown';
      
      // 2. Transfer miktarını doğru formatta hazırla
      const transferAmount = parseFloat(parseFloat(amount).toFixed(5));
      
      console.log("Transfer bilgileri:", {
        direction: transferDirection,
        amount: transferAmount,
        bankAccount: selectedBankAccount,
        bankName: bankAccountDetails?.bankName,
        bankBalance: bankAccountDetails?.balance,
        targetBankAccount: selectedTargetBankAccount,
        targetBankName: targetBankAccountDetails?.bankName,
        targetBankBalance: targetBankAccountDetails?.balance
      });
      
      // 3. Firebase toplu güncelleme işlemleri için boş bir nesne oluştur
      const updates: Record<string, any> = {};
      
      // 4. Yöne göre işlem yap
      if (transferDirection === 'bank_to_cash') {
        // A. BANKADAN KASAYA TRANSFER İŞLEMİ (SQL API)
        
        console.log('💸 Bankadan kasaya transfer başlatılıyor (SQL)...');
        
        // A1. Alt hesap ID'sini ayıkla (varsa)
        let bankAccountId = parseInt(selectedBankAccount, 10);
        let bankSubAccountId: number | null = null;
        
        if (bankAccountDetails?.isSubAccount && bankAccountDetails?.parentId) {
          // Alt hesap formatı: "parentId_subId"
          const parts = selectedBankAccount.split('_');
          bankAccountId = parseInt(bankAccountDetails.parentId, 10);
          bankSubAccountId = parts[1] ? parseInt(parts[1], 10) : null;
          
          console.log('📋 Alt hesap tespit edildi:', { bankAccountId, bankSubAccountId });
        }
        
        const cashboxId = parseInt(selectedCashbox, 10);
        
        // A2. Request body hazırla
        const transferRequest = {
          bankAccountId: bankAccountId,
          bankSubAccountId: bankSubAccountId,
          cashboxId: cashboxId,
          amount: transferAmount,
          description: description.trim() || `Bankadan kasaya transfer: ${bankAccountDetails?.bankName} → ${cashboxDetails?.name}`,
          documentNo: documentNo.trim() || null,
          createdBy: userEmail
        };
        
        console.log('📤 Transfer request:', transferRequest);
        
        // A3. SQL API'ye POST isteği gönder
        const response = await accountingApi.post(ACCOUNTING_API.BANK_ACCOUNT_TRANSFER_TO_CASH, transferRequest);
        const resultData = response.data;
        console.log('✅ Transfer başarılı (SQL):', resultData);
        
      } else if (transferDirection === 'cash_to_bank') {
        // B. KASADAN BANKAYA TRANSFER İŞLEMİ (SQL API)
        
        console.log('💸 Kasadan bankaya transfer başlatılıyor (SQL)...');
        
        // B1. Alt hesap ID'sini ayıkla (varsa)
        let bankAccountId = parseInt(selectedBankAccount, 10);
        let bankSubAccountId: number | null = null;
        
        if (bankAccountDetails?.isSubAccount && bankAccountDetails?.parentId) {
          // Alt hesap formatı: "parentId_subId"
          const parts = selectedBankAccount.split('_');
          bankAccountId = parseInt(bankAccountDetails.parentId, 10);
          bankSubAccountId = parts[1] ? parseInt(parts[1], 10) : null;
          
          console.log('📋 Alt hesap tespit edildi:', { bankAccountId, bankSubAccountId });
        }
        
        const cashboxId = parseInt(selectedCashbox, 10);
        
        // B2. Request body hazırla
        const transferRequest = {
          cashboxId: cashboxId,
          bankAccountId: bankAccountId,
          bankSubAccountId: bankSubAccountId,
          amount: transferAmount,
          description: description.trim() || `Kasadan bankaya transfer: ${cashboxDetails?.name} → ${bankAccountDetails?.bankName}`,
          documentNo: documentNo.trim() || null,
          createdBy: userEmail
        };
        
        console.log('📤 Transfer request:', transferRequest);
        
        // B3. SQL API'ye POST isteği gönder
        const response = await accountingApi.post(ACCOUNTING_API.BANK_ACCOUNT_TRANSFER_FROM_CASH, transferRequest);
        const resultData = response.data;
        console.log('✅ Transfer başarılı (SQL):', resultData);
        
      } else if (transferDirection === 'cash_to_cash') {
        // C. KASADAN KASAYA TRANSFER İŞLEMİ (SQL API)
        
        console.log('💸 Kasadan kasaya transfer başlatılıyor (SQL)...');
        
        const sourceCashboxId = parseInt(selectedCashbox, 10);
        const targetCashboxId = parseInt(selectedTargetCashbox, 10);
        
        // C1. Request body hazırla
        const transferRequest = {
          sourceCashboxId: sourceCashboxId,
          targetCashboxId: targetCashboxId,
          amount: transferAmount,
          description: description.trim() || `Kasadan kasaya transfer: ${cashboxDetails?.name} → ${targetCashboxDetails?.name}`,
          documentNo: documentNo.trim() || null,
          createdBy: userEmail
        };
        
        console.log('📤 Transfer request:', transferRequest);
        
        // C2. SQL API'ye POST isteği gönder
        const response = await accountingApi.post(ACCOUNTING_API.CASHBOX_TRANSFER, transferRequest);
        const resultData = response.data;
        console.log('✅ Transfer başarılı (SQL):', resultData);
        
      } else if (transferDirection === 'bank_to_bank') {
        // D. BANKADAN BANKAYA TRANSFER İŞLEMİ (SQL API)
        
        console.log('💸 Bankadan bankaya transfer başlatılıyor (SQL)...');
        
        // D1. Kaynak ve hedef hesap ID'lerini ayıkla
        let sourceBankAccountId = parseInt(selectedBankAccount, 10);
        let sourceBankSubAccountId: number | null = null;
        
        if (bankAccountDetails?.isSubAccount && bankAccountDetails?.parentId) {
          const parts = selectedBankAccount.split('_');
          sourceBankAccountId = parseInt(bankAccountDetails.parentId, 10);
          sourceBankSubAccountId = parts[1] ? parseInt(parts[1], 10) : null;
          console.log('📋 Kaynak alt hesap tespit edildi:', { sourceBankAccountId, sourceBankSubAccountId });
        }
        
        let targetBankAccountId = parseInt(selectedTargetBankAccount, 10);
        let targetBankSubAccountId: number | null = null;
        
        if (targetBankAccountDetails?.isSubAccount && targetBankAccountDetails?.parentId) {
          const parts = selectedTargetBankAccount.split('_');
          targetBankAccountId = parseInt(targetBankAccountDetails.parentId, 10);
          targetBankSubAccountId = parts[1] ? parseInt(parts[1], 10) : null;
          console.log('📋 Hedef alt hesap tespit edildi:', { targetBankAccountId, targetBankSubAccountId });
        }
        
        // D2. Request body hazırla
        const transferRequest = {
          sourceBankAccountId: sourceBankAccountId,
          sourceBankSubAccountId: sourceBankSubAccountId,
          targetBankAccountId: targetBankAccountId,
          targetBankSubAccountId: targetBankSubAccountId,
          amount: transferAmount,
          description: description.trim() || `Bankadan bankaya transfer: ${bankAccountDetails?.bankName} → ${targetBankAccountDetails?.bankName}`,
          documentNo: documentNo.trim() || null,
          createdBy: userEmail
        };
        
        console.log('📤 Transfer request:', transferRequest);
        
        // D3. SQL API'ye POST isteği gönder
        const response = await accountingApi.post(ACCOUNTING_API.BANK_ACCOUNT_TRANSFER, transferRequest);
        const resultData = response.data;
        console.log('✅ Transfer başarılı (SQL):', resultData);
        
      } else if (transferDirection === 'account_to_account') {
        // E. CARİ HESAPTAN CARİ HESABA TRANSFER İŞLEMİ (Mahsup Fişi - SQL API)
        
        console.log('💸 Cariden cariye transfer (Mahsup Fişi) başlatılıyor (SQL)...');
        
        // E1. Kaynak ve hedef cari ID'lerini ayıkla (SQL ID)
        const sourceAccountId = parseInt(selectedAccount, 10);
        const targetAccountId = parseInt(selectedTargetAccount, 10);
        
        if (isNaN(sourceAccountId) || isNaN(targetAccountId)) {
          throw new Error('Geçersiz cari hesap ID formatı');
        }
        
        console.log('📋 Cari hesaplar:', { sourceAccountId, targetAccountId });
        
        // E2. Request body hazırla
        const transferRequest = {
          sourceAccountId: sourceAccountId,
          targetAccountId: targetAccountId,
          amount: transferAmount,
          description: description.trim() || `Mahsup Fişi: ${accountDetails?.companyName} → ${targetAccountDetails?.companyName}`,
          documentNo: documentNo.trim() || null,
          createdBy: userEmail
        };
        
        console.log('📤 Mahsup Fişi request:', transferRequest);
        
        // E3. SQL API'ye POST isteği gönder
        const response = await accountingApi.post(ACCOUNTING_API.ACCOUNT_TRANSFER, transferRequest);
        const resultData = response.data;
        console.log('✅ Mahsup Fişi başarılı (SQL):', resultData);
        
      }
      
      console.log("Transfer işlemi tamamlandı (SQL)");
      
      // 6. Başarı mesajını göster
      let successText = '';
      if (transferDirection === 'bank_to_cash') {
        successText = `${transferAmount.toLocaleString('tr-TR')} ${bankAccountDetails?.currency} başarıyla bankadan kasaya transfer edildi.`;
      } else if (transferDirection === 'cash_to_bank') {
        successText = `${transferAmount.toLocaleString('tr-TR')} ${cashboxDetails?.currency} başarıyla kasadan bankaya transfer edildi.`;
      } else if (transferDirection === 'cash_to_cash') {
        successText = `${transferAmount.toLocaleString('tr-TR')} ${cashboxDetails?.currency} başarıyla kasadan kasaya transfer edildi.`;
      } else if (transferDirection === 'bank_to_bank') {
        successText = `${transferAmount.toLocaleString('tr-TR')} ${bankAccountDetails?.currency} başarıyla bankadan bankaya transfer edildi.`;
      } else if (transferDirection === 'account_to_account') {
        successText = `${transferAmount.toLocaleString('tr-TR')} TL tutarındaki mahsup fişi '${accountDetails?.companyName || accountDetails?.name}' hesabından '${targetAccountDetails?.companyName || targetAccountDetails?.name}' hesabına başarıyla aktarıldı.`;
      }
      
      setSuccessMessage(successText);
      console.log("Transfer başarılı:", successText);
      
      // 7. Kısa bir süre sonra modalı kapat
      setTimeout(() => {
        if (onSuccess) onSuccess();
        onClose();
      }, 1500);
      
    } catch (error: any) {
      console.error('Transfer işlemi sırasında hata oluştu:', error);
      
      // Daha fazla hata detayı yazdır
      if (error.code) {
        console.error('Hata kodu:', error.code);
      }
      if (error.message) {
        console.error('Hata mesajı:', error.message);
      }
      
      // Kullanıcıya gösterilecek hata mesajı
      let errorMessage = 'Transfer işlemi sırasında bir hata oluştu. ';
      
      // Hata tipine göre mesaj ekle
      if (error.code === 'PERMISSION_DENIED') {
        errorMessage += 'Yetki hatası: Bu işlemi gerçekleştirme izniniz yok.';
      } else if (error.code === 'NETWORK_ERROR') {
        errorMessage += 'Ağ hatası: İnternet bağlantınızı kontrol edin.';
      } else if (error.response?.data?.message) {
        errorMessage += `Hata detayı: ${error.response.data.message}`;
      } else if (error.message) {
        errorMessage += `Hata detayı: ${error.message}`;
      } else {
        errorMessage += 'Lütfen tekrar deneyin veya sistem yöneticisiyle iletişime geçin.';
      }
      
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Yön değiştirme butonu
  const handleSwapDirection = () => {
    if (transferDirection === 'bank_to_cash') {
      // Bankadan kasaya -> Kasadan bankaya
      setTransferDirection('cash_to_bank');
      // Seçili hesapları sıfırla
      setSelectedBankAccount('');
      setSelectedCashbox('');
      setBankAccountDetails(null);
      setCashboxDetails(null);
    } else if (transferDirection === 'cash_to_bank') {
      // Kasadan bankaya -> Bankadan kasaya
      setTransferDirection('bank_to_cash');
      // Seçili hesapları sıfırla
      setSelectedBankAccount('');
      setSelectedCashbox('');
      setBankAccountDetails(null);
      setCashboxDetails(null);
    } else if (transferDirection === 'cash_to_cash') {
      // Kasadan kasaya -> Kasadan kasaya (kaynak ve hedef yer değiştirir)
      const tempSourceCashbox = selectedCashbox;
      const tempSourceDetails = cashboxDetails;
      const tempTargetCashbox = selectedTargetCashbox;
      const tempTargetDetails = targetCashboxDetails;

      setSelectedCashbox(tempTargetCashbox);
      setCashboxDetails(tempTargetDetails);
      setSelectedTargetCashbox(tempSourceCashbox);
      setTargetCashboxDetails(tempSourceDetails);
    } else if (transferDirection === 'bank_to_bank') {
      // Bankadan bankaya -> Bankadan bankaya (kaynak ve hedef yer değiştirir)
      const tempSourceBank = selectedBankAccount;
      const tempSourceDetails = bankAccountDetails;
      const tempTargetBank = selectedTargetBankAccount;
      const tempTargetDetails = targetBankAccountDetails;

      setSelectedBankAccount(tempTargetBank);
      setBankAccountDetails(tempTargetDetails);
      setSelectedTargetBankAccount(tempSourceBank);
      setTargetBankAccountDetails(tempTargetDetails);
    }
  };

  // Kaynak bilgisi gösterimi
  const renderSourceInfo = () => {
    if (transferDirection === 'bank_to_cash' || transferDirection === 'bank_to_bank') {
      return bankAccountDetails ? (
        <Box sx={{ border: '1px solid #e0e0e0', p: 2, borderRadius: 1, backgroundColor: '#f9f9f9' }}>
          <Typography variant="subtitle2" gutterBottom>
            <AccountBalanceIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
            Kaynak: Banka Hesabı
          </Typography>
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 'medium' }}>
            {bankAccountDetails.bankName} - {bankAccountDetails.accountNumber}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Mevcut Bakiye: <strong>{bankAccountDetails.balance.toLocaleString('tr-TR')} {getCurrencySymbol(bankAccountDetails.currency)}</strong>
          </Typography>
        </Box>
      ) : null;
    } else if (transferDirection === 'account_to_account') {
      // Cari hesaplar için yeni oluşturduğumuz fonksiyonu çağır
      return renderAccountSourceInfo();
    } else {
      return cashboxDetails ? (
        <Box sx={{ border: '1px solid #e0e0e0', p: 2, borderRadius: 1, backgroundColor: '#f9f9f9' }}>
          <Typography variant="subtitle2" gutterBottom>
            <AccountBalanceWalletIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
            Kaynak: Kasa
          </Typography>
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 'medium' }}>
            {cashboxDetails.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Mevcut Bakiye: <strong>{cashboxDetails.balance.toLocaleString('tr-TR')} {getCurrencySymbol(cashboxDetails.currency)}</strong>
          </Typography>
        </Box>
      ) : null;
    }
  };

  // Hedef bilgisi gösterimi
  const renderTargetInfo = () => {
    if (transferDirection === 'bank_to_cash') {
      return cashboxDetails ? (
        <Box sx={{ border: '1px solid #e0e0e0', p: 2, borderRadius: 1, backgroundColor: '#f9f9f9' }}>
          <Typography variant="subtitle2" gutterBottom>
            <AccountBalanceWalletIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
            Hedef: Kasa
          </Typography>
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 'medium' }}>
            {cashboxDetails.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Mevcut Bakiye: <strong>{cashboxDetails.balance.toLocaleString('tr-TR')} {getCurrencySymbol(cashboxDetails.currency)}</strong>
          </Typography>
        </Box>
      ) : null;
    } else if (transferDirection === 'cash_to_bank') {
      return bankAccountDetails ? (
        <Box sx={{ border: '1px solid #e0e0e0', p: 2, borderRadius: 1, backgroundColor: '#f9f9f9' }}>
          <Typography variant="subtitle2" gutterBottom>
            <AccountBalanceIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
            Hedef: Banka Hesabı
          </Typography>
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 'medium' }}>
            {bankAccountDetails.bankName} - {bankAccountDetails.accountNumber}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Mevcut Bakiye: <strong>{bankAccountDetails.balance.toLocaleString('tr-TR')} {getCurrencySymbol(bankAccountDetails.currency)}</strong>
          </Typography>
        </Box>
      ) : null;
    } else if (transferDirection === 'bank_to_bank') {
      return targetBankAccountDetails ? (
        <Box sx={{ border: '1px solid #e0e0e0', p: 2, borderRadius: 1, backgroundColor: '#f9f9f9' }}>
          <Typography variant="subtitle2" gutterBottom>
            <AccountBalanceIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
            Hedef: Banka Hesabı
          </Typography>
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 'medium' }}>
            {targetBankAccountDetails.bankName} - {targetBankAccountDetails.accountNumber}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Mevcut Bakiye: <strong>{targetBankAccountDetails.balance.toLocaleString('tr-TR')} {getCurrencySymbol(targetBankAccountDetails.currency)}</strong>
          </Typography>
        </Box>
      ) : null;
    } else if (transferDirection === 'account_to_account') {
      // Cari hesaplar için yeni oluşturduğumuz fonksiyonu çağır
      return renderAccountTargetInfo();
    } else { // cash_to_cash
      return targetCashboxDetails ? (
        <Box sx={{ border: '1px solid #e0e0e0', p: 2, borderRadius: 1, backgroundColor: '#f9f9f9' }}>
          <Typography variant="subtitle2" gutterBottom>
            <AccountBalanceWalletIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
            Hedef: Kasa
          </Typography>
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 'medium' }}>
            {targetCashboxDetails.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Mevcut Bakiye: <strong>{targetCashboxDetails.balance.toLocaleString('tr-TR')} {getCurrencySymbol(targetCashboxDetails.currency)}</strong>
          </Typography>
        </Box>
      ) : null;
    }
  };

  // Para birimi uyuşmazlığı kontrolü
  const currencyMismatch = 
    (transferDirection === 'bank_to_cash' || transferDirection === 'cash_to_bank') ? 
      (bankAccountDetails && cashboxDetails && bankAccountDetails.currency !== cashboxDetails.currency) :
    (transferDirection === 'cash_to_cash') ?
      (cashboxDetails && targetCashboxDetails && cashboxDetails.currency !== targetCashboxDetails.currency) :
    (transferDirection === 'bank_to_bank') ?
      (bankAccountDetails && targetBankAccountDetails && bankAccountDetails.currency !== targetBankAccountDetails.currency) :
      (accountDetails && targetAccountDetails && accountDetails.currency !== targetAccountDetails.currency);

  // Kaynak bilgisi gösterimi (cari hesaplar için)
  const renderAccountSourceInfo = () => {
    return accountDetails ? (
      <Box sx={{ border: '1px solid #e0e0e0', p: 2, borderRadius: 1, backgroundColor: '#f9f9f9' }}>
        <Typography variant="subtitle2" gutterBottom>
          <AccountBalanceIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
          Kaynak: Cari Hesap
        </Typography>
        <Typography variant="body2" gutterBottom sx={{ fontWeight: 'medium' }}>
          {accountDetails.code} - {accountDetails.companyName || accountDetails.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Alacak Bakiyesi (creditBalance): <strong>{accountDetails.creditBalance.toLocaleString('tr-TR')} {getCurrencySymbol(accountDetails.currency)}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Borç Bakiyesi (debitBalance): <strong>{accountDetails.debitBalance.toLocaleString('tr-TR')} {getCurrencySymbol(accountDetails.currency)}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Hesap Türü: <strong>{accountDetails.type === 'customer' ? 'Müşteri' : 'Tedarikçi'}</strong>
        </Typography>
      </Box>
    ) : null;
  };

  // Hedef bilgisi gösterimi (cari hesaplar için)
  const renderAccountTargetInfo = () => {
    return targetAccountDetails ? (
      <Box sx={{ border: '1px solid #e0e0e0', p: 2, borderRadius: 1, backgroundColor: '#f9f9f9' }}>
        <Typography variant="subtitle2" gutterBottom>
          <AccountBalanceIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
          Hedef: Cari Hesap
        </Typography>
        <Typography variant="body2" gutterBottom sx={{ fontWeight: 'medium' }}>
          {targetAccountDetails.code} - {targetAccountDetails.companyName || targetAccountDetails.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Alacak Bakiyesi (creditBalance): <strong>{targetAccountDetails.creditBalance.toLocaleString('tr-TR')} {getCurrencySymbol(targetAccountDetails.currency)}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Borç Bakiyesi (debitBalance): <strong>{targetAccountDetails.debitBalance.toLocaleString('tr-TR')} {getCurrencySymbol(targetAccountDetails.currency)}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Hesap Türü: <strong>{targetAccountDetails.type === 'customer' ? 'Müşteri' : 'Tedarikçi'}</strong>
        </Typography>
      </Box>
    ) : null;
  };

  // Bakiye yetersiz uyarıları
  const insufficientBalanceError = 
    (transferDirection === 'bank_to_cash' && 
     bankAccountDetails && parseFloat(amount) > bankAccountDetails.balance) ? 
      `Banka hesabında yeterli bakiye bulunmuyor (Mevcut: ${bankAccountDetails.balance} ${getCurrencySymbol(bankAccountDetails.currency)})` :
    (transferDirection === 'cash_to_bank' && 
     cashboxDetails && parseFloat(amount) > cashboxDetails.balance) ?
      `Kasada yeterli bakiye bulunmuyor (Mevcut: ${cashboxDetails.balance} ${getCurrencySymbol(cashboxDetails.currency)})` :
    (transferDirection === 'cash_to_cash' && 
     cashboxDetails && parseFloat(amount) > cashboxDetails.balance) ?
      `Kaynak kasada yeterli bakiye bulunmuyor (Mevcut: ${cashboxDetails.balance} ${getCurrencySymbol(cashboxDetails.currency)})` :
    (transferDirection === 'bank_to_bank' && 
     bankAccountDetails && parseFloat(amount) > bankAccountDetails.balance) ?
      `Kaynak banka hesabında yeterli bakiye bulunmuyor (Mevcut: ${bankAccountDetails.balance} ${getCurrencySymbol(bankAccountDetails.currency)})` :
    (transferDirection === 'account_to_account' && 
     accountDetails?.creditBalance && accountDetails.creditBalance > 0 && 
     parseFloat(amount) > accountDetails.creditBalance) ?
      `Kaynak cari hesapta yeterli alacak bakiyesi bulunmuyor (Mevcut: ${accountDetails.creditBalance} ${getCurrencySymbol(accountDetails.currency)})` :
    (transferDirection === 'account_to_account' && 
     accountDetails?.debitBalance && accountDetails.debitBalance > 0 && 
     parseFloat(amount) > accountDetails.debitBalance) ?
      `Kaynak cari hesapta yeterli borç bakiyesi bulunmuyor (Mevcut: ${accountDetails.debitBalance} ${getCurrencySymbol(accountDetails.currency)})` :
    null;

  return (
    <Modal 
      open={open} 
      onClose={!saving ? onClose : undefined}
      aria-labelledby="virman-transfer-modal"
    >
      <Paper 
        sx={{ 
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 800, md: 1000 },
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          maxHeight: '90vh',
          overflowY: 'auto'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h2" fontWeight="bold" color="primary">
            Para Transfer İşlemleri
          </Typography>
          <IconButton onClick={onClose} size="small" disabled={saving}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Transfer Yönü Seçimi */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="transfer-direction-label">Transfer Yönü</InputLabel>
          <Select
            labelId="transfer-direction-label"
            value={transferDirection}
            label="Transfer Yönü"
            onChange={handleDirectionChange}
          >
            <MenuItem value="bank_to_cash">Bankadan Kasaya</MenuItem>
            <MenuItem value="cash_to_bank">Kasadan Bankaya</MenuItem>
            <MenuItem value="cash_to_cash">Kasadan Kasaya</MenuItem>
            <MenuItem value="bank_to_bank">Bankadan Bankaya</MenuItem>
            <MenuItem value="account_to_account" disabled>Cariden Cariye (Geçici Kapalı)</MenuItem>
          </Select>
        </FormControl>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={3}>
              {/* Kaynak Hesap Seçimi */}
              <Grid item xs={12} md={5}>
                {transferDirection === 'bank_to_cash' || transferDirection === 'bank_to_bank' ? (
                  <Autocomplete
                    options={bankAccounts}
                    getOptionLabel={(option) => `${option.bankName} - ${option.accountNumber} (${getCurrencySymbol(option.currency)})`}
                    value={bankAccounts.find(acc => acc.id === selectedBankAccount) || null}
                    onChange={(event, newValue) => {
                      if (newValue) {
                        setSelectedBankAccount(newValue.id);
                        setBankAccountDetails(newValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                      label="Kaynak Banka Hesabı"
                      error={bankAccounts.length === 0}
                        helperText={bankAccounts.length === 0 ? 'Banka hesabı bulunamadı' : ''}
                        sx={{ 
                          '& .MuiInputBase-root': { 
                            minHeight: '56px',
                            fontSize: '1rem'
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '1rem'
                          }
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {option.bankName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {option.accountNumber} - {getCurrencySymbol(option.currency)}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    fullWidth
                  />
                ) : transferDirection === 'account_to_account' ? (
                  <Autocomplete
                    options={accounts}
                    getOptionLabel={(option) => {
                      // Hesap kodu AUTO- ile başlıyorsa gösterme
                      const showCode = option.code && !option.code.startsWith('AUTO-');
                      return showCode 
                        ? `${option.code} - ${option.companyName || option.name}`
                        : `${option.companyName || option.name}`;
                    }}
                    value={accounts.find(acc => acc.id === selectedAccount) || null}
                    onChange={(event, newValue) => {
                      if (newValue) {
                        setSelectedAccount(newValue.id);
                        setAccountDetails(newValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Kaynak Cari Hesap"
                        error={accounts.length === 0}
                        helperText={accounts.length === 0 ? 'Cari hesap bulunamadı' : ''}
                        sx={{ 
                          '& .MuiInputBase-root': { 
                            minHeight: '56px',
                            fontSize: '1rem'
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '1rem'
                          }
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {option.companyName || option.name}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              {option.code && !option.code.startsWith('AUTO-') ? `Kod: ${option.code}` : 'Otomatik Kod'}
                            </Typography>
                            <Typography variant="body2" color="primary" sx={{ fontWeight: 'medium' }}>
                              Bakiye: {option.balance.toLocaleString('tr-TR')} {getCurrencySymbol(option.currency)}
                            </Typography>
                          </Box>
                        </Box>
                      </li>
                    )}
                    fullWidth
                  />
                ) : (
                  <Autocomplete
                    options={cashboxes}
                    getOptionLabel={(option) => `${option.name} (${getCurrencySymbol(option.currency)})`}
                    value={cashboxes.find(cash => cash.id === selectedCashbox) || null}
                    onChange={(event, newValue) => {
                      if (newValue) {
                        setSelectedCashbox(newValue.id);
                        setCashboxDetails(newValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                      label="Kaynak Kasa"
                      error={cashboxes.length === 0}
                        helperText={cashboxes.length === 0 ? 'Kasa bulunamadı' : ''}
                        sx={{ 
                          '& .MuiInputBase-root': { 
                            minHeight: '56px',
                            fontSize: '1rem'
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '1rem'
                          }
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {option.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {getCurrencySymbol(option.currency)}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    fullWidth
                  />
                )}
              </Grid>

              {/* Yön Değiştirme Butonu */}
              <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <IconButton
                  onClick={handleSwapDirection}
                  color="primary"
                  sx={{
                    border: '1px dashed',
                    p: 1.5,
                    borderRadius: '50%',
                    transform: transferDirection === 'cash_to_bank' ? 'rotate(180deg)' : 'none',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                >
                  <SwapHorizIcon />
                </IconButton>
              </Grid>

              {/* Hedef Hesap Seçimi */}
              <Grid item xs={12} md={5}>
                {transferDirection === 'bank_to_cash' ? (
                  <Autocomplete
                    options={cashboxes}
                    getOptionLabel={(option) => `${option.name} (${getCurrencySymbol(option.currency)})`}
                    value={cashboxes.find(cash => cash.id === selectedCashbox) || null}
                    onChange={(event, newValue) => {
                      if (newValue) {
                        setSelectedCashbox(newValue.id);
                        setCashboxDetails(newValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                      label="Hedef Kasa"
                      error={cashboxes.length === 0}
                        helperText={cashboxes.length === 0 ? 'Kasa bulunamadı' : ''}
                        sx={{ 
                          '& .MuiInputBase-root': { 
                            minHeight: '56px',
                            fontSize: '1rem'
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '1rem'
                          }
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {option.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {getCurrencySymbol(option.currency)}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    fullWidth
                  />
                ) : transferDirection === 'cash_to_bank' ? (
                  <Autocomplete
                    options={bankAccounts}
                    getOptionLabel={(option) => `${option.bankName} - ${option.accountNumber} (${getCurrencySymbol(option.currency)})`}
                    value={bankAccounts.find(acc => acc.id === selectedBankAccount) || null}
                    onChange={(event, newValue) => {
                      if (newValue) {
                        setSelectedBankAccount(newValue.id);
                        setBankAccountDetails(newValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                      label="Hedef Banka Hesabı"
                      error={bankAccounts.length === 0}
                        helperText={bankAccounts.length === 0 ? 'Banka hesabı bulunamadı' : ''}
                        sx={{ 
                          '& .MuiInputBase-root': { 
                            minHeight: '56px',
                            fontSize: '1rem'
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '1rem'
                          }
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {option.bankName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {option.accountNumber} - {getCurrencySymbol(option.currency)}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    fullWidth
                  />
                ) : transferDirection === 'cash_to_cash' ? (
                  <Autocomplete
                    options={cashboxes.filter(cashbox => cashbox.id !== selectedCashbox)}
                    getOptionLabel={(option) => `${option.name} (${getCurrencySymbol(option.currency)})`}
                    value={cashboxes.find(cash => cash.id === selectedTargetCashbox) || null}
                    onChange={(event, newValue) => {
                      if (newValue) {
                        setSelectedTargetCashbox(newValue.id);
                        setTargetCashboxDetails(newValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                      label="Hedef Kasa"
                        error={cashboxes.length <= 1}
                        helperText={cashboxes.length <= 1 ? 'Transfer için yeterli kasa bulunamadı' : ''}
                        sx={{ 
                          '& .MuiInputBase-root': { 
                            minHeight: '56px',
                            fontSize: '1rem'
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '1rem'
                          }
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {option.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {getCurrencySymbol(option.currency)}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    fullWidth
                  />
                ) : transferDirection === 'bank_to_bank' ? (
                  <Autocomplete
                    options={bankAccounts.filter(account => account.id !== selectedBankAccount)}
                    getOptionLabel={(option) => `${option.bankName} - ${option.accountNumber} (${getCurrencySymbol(option.currency)})`}
                    value={bankAccounts.find(acc => acc.id === selectedTargetBankAccount) || null}
                    onChange={(event, newValue) => {
                      if (newValue) {
                        setSelectedTargetBankAccount(newValue.id);
                        setTargetBankAccountDetails(newValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Hedef Banka Hesabı"
                        error={bankAccounts.length <= 1}
                        helperText={bankAccounts.length <= 1 ? 'Transfer için yeterli banka hesabı bulunamadı' : ''}
                        sx={{ 
                          '& .MuiInputBase-root': { 
                            minHeight: '56px',
                            fontSize: '1rem'
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '1rem'
                          }
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {option.bankName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {option.accountNumber} - {getCurrencySymbol(option.currency)}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    fullWidth
                  />
                ) : transferDirection === 'account_to_account' ? (
                  <Autocomplete
                    options={accounts.filter(account => account.id !== selectedAccount)}
                    getOptionLabel={(option) => {
                      // Hesap kodu AUTO- ile başlıyorsa gösterme
                      const showCode = option.code && !option.code.startsWith('AUTO-');
                      return showCode 
                        ? `${option.code} - ${option.companyName || option.name}`
                        : `${option.companyName || option.name}`;
                    }}
                    value={accounts.find(acc => acc.id === selectedTargetAccount) || null}
                    onChange={(event, newValue) => {
                      if (newValue) {
                        setSelectedTargetAccount(newValue.id);
                        setTargetAccountDetails(newValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Hedef Cari Hesap"
                        error={accounts.length <= 1}
                        helperText={accounts.length <= 1 ? 'Transfer için yeterli cari hesap bulunamadı' : ''}
                        sx={{ 
                          '& .MuiInputBase-root': { 
                            minHeight: '56px',
                            fontSize: '1rem'
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '1rem'
                          }
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {option.companyName || option.name}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              {option.code && !option.code.startsWith('AUTO-') ? `Kod: ${option.code}` : 'Otomatik Kod'}
                            </Typography>
                            <Typography variant="body2" color="primary" sx={{ fontWeight: 'medium' }}>
                              Bakiye: {option.balance.toLocaleString('tr-TR')} {getCurrencySymbol(option.currency)}
                            </Typography>
                          </Box>
                        </Box>
                      </li>
                    )}
                    fullWidth
                  />
                ) : null}
              </Grid>
            </Grid>

            {/* Para Birimi Uyarısı */}
            {currencyMismatch && (
              <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                Transfer işlemi sadece aynı para birimindeki hesaplar arasında yapılabilir. 
                Seçilen hesapların para birimleri farklı.
              </Alert>
            )}

            {/* Bilgi Gösterimi */}
            <Box sx={{ my: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  {renderSourceInfo()}
                </Grid>
                <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <SwapHorizIcon color="primary" />
                </Grid>
                <Grid item xs={12} md={5}>
                  {renderTargetInfo()}
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Transfer İşlem Bilgileri */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  autoFocus
                  required
                  fullWidth
                  label="Transfer Miktarı"
                  value={amount}
                  onChange={handleAmountChange}
                  type="text"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {bankAccountDetails ? getCurrencySymbol(bankAccountDetails.currency) : ''}
                      </InputAdornment>
                    ),
                  }}
                  error={!!error && !amount}
                  helperText={!amount && !!error ? 'Bu alan zorunludur' : ''}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Belge/Dekont No"
                  value={documentNo}
                  onChange={(e) => setDocumentNo(e.target.value)}
                  placeholder="Varsa belge numarası"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Açıklama"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  rows={3}
                  placeholder="İsteğe bağlı açıklama ekleyebilirsiniz"
                />
              </Grid>
            </Grid>

            {successMessage && (
              <Alert severity="success" sx={{ mt: 2, mb: 1 }}>
                {successMessage}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2, mb: 1 }}>
                {error}
              </Alert>
            )}

            {/* Bakiye yetersiz uyarısı */}
            {!error && insufficientBalanceError && (
              <Alert severity="warning" sx={{ mt: 2, mb: 1 }}>
                {insufficientBalanceError}
              </Alert>
            )}
          </>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={onClose}
            disabled={saving}
          >
            İptal
          </Button>
          <Button 
            variant="contained" 
            onClick={handleTransfer}
            disabled={
              !amount || 
              saving || 
              parseFloat(amount) <= 0 || 
              !!currencyMismatch || 
              (transferDirection === 'bank_to_cash' && (!selectedBankAccount || !selectedCashbox)) ||
              (transferDirection === 'cash_to_bank' && (!selectedCashbox || !selectedBankAccount)) ||
              (transferDirection === 'cash_to_cash' && (!selectedCashbox || !selectedTargetCashbox)) ||
              (transferDirection === 'bank_to_bank' && (!selectedBankAccount || !selectedTargetBankAccount)) ||
              !!(transferDirection === 'bank_to_cash' && 
                 bankAccountDetails && 
                 parseFloat(amount) > bankAccountDetails.balance) ||
              !!(transferDirection === 'cash_to_bank' && 
                 cashboxDetails && 
                 parseFloat(amount) > cashboxDetails.balance) ||
              !!(transferDirection === 'cash_to_cash' && 
                 cashboxDetails && 
                 parseFloat(amount) > cashboxDetails.balance) ||
              !!(transferDirection === 'bank_to_bank' && 
                 bankAccountDetails && 
                 parseFloat(amount) > bankAccountDetails.balance)
            }
            startIcon={saving ? <CircularProgress size={24} /> : undefined}
          >
            {saving ? 'İşleniyor...' : 'Transfer Yap'}
          </Button>
        </Box>
      </Paper>
    </Modal>
  );
};

export default CashTransferModal; 